import { type OrderForWorklist } from '@/Models/GenericWorklist/WorklistResult';
import WorklistSortStore from '@/Pages/GenericWorklist/Stores/WorklistSortStore';
import { Header } from '@tanstack/react-table';
import classNames from 'classnames';
import { observer } from 'mobx-react-lite';
import { useMemo } from 'react';
import Style from './WorklistDataGridHeader.module.scss';
import { isWorklistColumn, isWorklistColumnSortable, WorklistColumn } from '@/Pages/GenericWorklist/Logic/WorklistProperties';
import { DobTableHeader, DobTableHeaderCell, DobTableRow } from '@dedalusdiit/dob-components';
import { IWorklistSelectionHelper } from '@/Pages/GenericWorklist/Stores/WorklistSelectionHelper';

export interface WorklistDataGridHeaderProps {
    headers: Header<OrderForWorklist, unknown>[];
    worklistSortStore: WorklistSortStore;
    selectionStore: IWorklistSelectionHelper;
}

const {
    'header-row': headerRowStyle,
    header: headerStyle,
    sortable: sortableStyle,
    title: headerTitleStyle,
    'sortable-disabled': sortableDisabledStyle,
    'selection-header': selectionHeaderStyle,
} = Style;

const ColumnHeader = (props: {
    column: WorklistColumn;
    columnHeaderText: React.ReactElement | string;
    isSortingDisabled: boolean;
    onCellClick?: (event: React.MouseEvent) => void;
}): React.ReactElement => {
    const { column, columnHeaderText, isSortingDisabled, onCellClick } = props;

    const isColumnSortable = isWorklistColumnSortable(column);

    const classes = classNames({
        [`${sortableStyle}`]: isColumnSortable,
        [sortableDisabledStyle]: isColumnSortable && isSortingDisabled,
    });

    const headerCellClasses = classNames(headerStyle, {
        [sortableDisabledStyle]: isColumnSortable && isSortingDisabled,
        [selectionHeaderStyle]: column === 'IsSelected',
    });

    return (
        <DobTableHeaderCell
            columnKey={column}
            className={headerCellClasses}
            sortable={isColumnSortable}
            {...(onCellClick !== undefined && { onClick: onCellClick })}
        >
            <div className={classes}>
                <span className={headerTitleStyle}>{columnHeaderText}</span>
            </div>
        </DobTableHeaderCell>
    );
};

const WorklistDataGridHeader = observer((props: WorklistDataGridHeaderProps): React.ReactElement => {
    const { headers: columnHeaders, worklistSortStore, selectionStore } = props;
    const { currentWorklistSort, isSortingDisabled } = worklistSortStore;

    const columnHeaderElements = useMemo(() => columnHeaders.map((header) => {
        const columnDefinition = header.column.columnDef as { id?: string; header: string | ((context: any) => string) };
        const columnID = columnDefinition.id!;
        const columnHeaderText = typeof columnDefinition.header === 'function'
            ? columnDefinition.header(header.getContext())
            : columnDefinition.header;

        if (!isWorklistColumn(columnID)) {
            throw new Error();
        }

        const onCellClick = columnID === 'IsSelected'
            ? (event: React.MouseEvent) => {
                    event.stopPropagation();
                    selectionStore.toggleSelectAll();
                }
            : undefined;

        return (
            <ColumnHeader
                key={columnID}
                column={columnID}
                columnHeaderText={columnHeaderText}
                isSortingDisabled={isSortingDisabled}
                onCellClick={onCellClick}
            />
        );
    }), [columnHeaders, currentWorklistSort, isSortingDisabled, selectionStore]);

    return (
        <DobTableHeader>
            <DobTableRow key="headerRow" className={headerRowStyle}>
                {columnHeaderElements}
            </DobTableRow>
        </DobTableHeader>
    );
});

export default WorklistDataGridHeader;
